import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { offlineEvents, pendingCount, syncOutbox, warmOfflineCache, resolveConflict as engineResolve } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";

const SyncContext = createContext(null);

export function SyncProvider({ children }) {
  const auth = useAuth();
  const user = auth?.user;
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [justSynced, setJustSynced] = useState(false);
  const syncingRef = useRef(false);

  const refreshPending = useCallback(async () => {
    try { setPending(await pendingCount()); } catch { /* IndexedDB unavailable */ }
  }, []);

  const runSync = useCallback(async () => {
    if (syncingRef.current) return;
    try {
      if ((await pendingCount()) === 0) return;
    } catch { return; }
    syncingRef.current = true;
    setSyncing(true);
    try {
      const res = await syncOutbox();
      setOnline(!res.offline);
      if (res.conflicts.length) {
        setConflicts((cur) => {
          const known = new Set(cur.map((c) => c.outboxId));
          return [...cur, ...res.conflicts.filter((c) => !known.has(c.outboxId))];
        });
      }
      if (res.synced > 0 && !res.offline) {
        setJustSynced(true);
        setTimeout(() => setJustSynced(false), 4000);
        // Let open pages refetch fresh server data (real ids replace temp ids)
        window.dispatchEvent(new CustomEvent("julia-synced"));
        // Refresh the full offline copy now that the queue is drained
        warmOfflineCache(true);
      }
    } finally {
      syncingRef.current = false;
      setSyncing(false);
      refreshPending();
    }
  }, [refreshPending]);

  useEffect(() => {
    refreshPending();
    const onQueued = () => refreshPending();
    const onDown = () => setOnline(false);
    const onOk = () => setOnline(true);
    offlineEvents.addEventListener("queued", onQueued);
    offlineEvents.addEventListener("network-down", onDown);
    offlineEvents.addEventListener("network-ok", onOk);
    const goOnline = () => { setOnline(true); runSync(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    runSync(); // drain anything left over from a previous session
    const interval = setInterval(() => {
      if (navigator.onLine) {
        runSync();
        warmOfflineCache(); // throttled internally — keeps the full offline copy fresh
      }
    }, 20000);
    return () => {
      offlineEvents.removeEventListener("queued", onQueued);
      offlineEvents.removeEventListener("network-down", onDown);
      offlineEvents.removeEventListener("network-ok", onOk);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(interval);
    };
  }, [runSync, refreshPending]);

  const resolveConflict = useCallback(async (conflict, keep) => {
    await engineResolve(conflict, keep);
    setConflicts((cur) => cur.filter((c) => c.outboxId !== conflict.outboxId));
    await refreshPending();
    window.dispatchEvent(new CustomEvent("julia-synced"));
  }, [refreshPending]);

  // Full offline copy: download every list, board, photo and document right
  // after login so the app works in dead-zones even for unvisited pages.
  useEffect(() => {
    if (user && typeof user === "object" && navigator.onLine) {
      warmOfflineCache();
    }
  }, [user]);

  const value = useMemo(
    () => ({ online, pending, syncing, conflicts, justSynced, runSync, resolveConflict }),
    [online, pending, syncing, conflicts, justSynced, runSync, resolveConflict]
  );

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export const useSync = () => useContext(SyncContext);
